import { FC, useMemo } from 'react';

import { ColumnTable, Entry } from '@/components/ColumnTable';
import { useSavedQuery } from '@/hooks/dbtStrategy/useSavedQuery';
import { useLinkToExplorer } from '@/hooks/links';
import { useUrlProjectParams } from '@/hooks/params';
import { useCommonLineageEntries } from '@/hooks/useCommonLineageEntries';
import { PanelDetails, ResourceDetailsParams } from './panelDetailTypes';
import { TagLinks } from './TagLinks';

export const useSavedQueryDetailsPanel = ({
  uniqueId,
  projectName,
}: ResourceDetailsParams): PanelDetails => {
  const savedQueryQuery = useSavedQuery(uniqueId);
  const projectScope = useUrlProjectParams();

  const commonEntries = useCommonLineageEntries(projectName);

  const getGroupLink = useLinkToExplorer('resourceDetails');

  return useMemo(() => {
    const savedQuery = savedQueryQuery.data.definition;
    if (!savedQuery) {
      return { ...savedQueryQuery, rows: null, description: null };
    }

    const rows: Entry[] = [
      ...commonEntries,
      {
        key: 'Tags',
        data: <TagLinks tags={savedQuery.tags} />,
      },
      {
        key: 'Group',
        data: savedQuery.group,
        link: getGroupLink({
          ...projectScope,
          dbtUniqueId: `group.${uniqueId.packageName}.${savedQuery.group}`,
        }),
      },
      { key: 'Package', data: uniqueId.packageName },
    ];

    return { ...savedQueryQuery, rows, description: savedQuery.description };
  }, [
    commonEntries,
    getGroupLink,
    savedQueryQuery,
    projectScope,
    uniqueId.packageName,
  ]);
};

export const SavedQueryDetailsTable: FC<ResourceDetailsParams> = ({
  className,
  children,
  compact,
  ...props
}) => {
  const savedQueryDetails = useSavedQueryDetailsPanel(props);

  return (
    <div className={className}>
      {children?.(savedQueryDetails)}
      <ColumnTable
        tableEntries={savedQueryDetails.rows}
        compact={compact}
        isLoading={savedQueryDetails.fetching}
      />
    </div>
  );
};
